import { action, computed, flow, makeObservable, observable } from 'mobx';
import { GlobalStore } from '@roc/feature-app-core';
import { FormStore } from '@roc/feature-app-core';
import { GridStore } from '@roc/feature-app-core';
import { Document } from '@roc/feature-types';
import { formatDate, parseAddress } from '@roc/feature-utils';
import { BorrowerEntityService } from './../../../services/documentForms/borrowerEntityService';
import { DocumentStore } from '../../documentStore';
import { differenceInYears } from 'date-fns';
import EditEntityBorrowerStore from './editEntityBorrowerStore';
import { LoanStore } from '@roc/feature-loans';
import { AIDocType } from '../../../constants';
import { ReviewLogStore } from '@roc/feature-ai-docs-review';
import { BORROWER_ENTITY_AUTOMATION_SUCCESS_STATUS,BORROWER_ENTITY_AUTOMATION_IN_PROGRESS_STATUS } from '../../../constants';
import defaultIcon from 'react-native-paper/lib/typescript/components/MaterialCommunityIcon';

const REQUIRED_MESSAGE = 'This field is required';

const form = {
  fields: {
    // Entity Information
    borrowerEntityOption: {
      value: null,
      error: null,
      rule: '',
    },
    name: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_MESSAGE,
    },
    type: {
      value: '',
      error: null,
      rule: '',
      message: REQUIRED_MESSAGE,
    },
    ein: {
      value: '',
      error: null,
      rule: 'regex:/^\\d{2}-\\d{7}$/',
      message: 'The EIN must have the format xx-xxxxxxx',
    },

    //Entity Address
    address: {
      value: '',
      error: null,
      rule: '',
      message: REQUIRED_MESSAGE,
    },
    streetNumber: {
      value: '',
      error: null,
      rule: '',
      message: REQUIRED_MESSAGE,
    },
    streetName: {
      value: '',
      error: null,
      rule: '',
      message: REQUIRED_MESSAGE,
    },
    addressLine2: {
      value: '',
      error: null,
      rule: '',
    },
    state: {
      value: '',
      error: null,
      rule: '',
      message: REQUIRED_MESSAGE,
    },
    city: {
      value: '',
      error: null,
      rule: '',
      message: REQUIRED_MESSAGE,
    },
    zip: {
      value: '',
      error: null,
      rule: '',
      message: REQUIRED_MESSAGE,
    },

    //Entity Details
    cogsDate: {
      value: null,
      error: null,
      rule: '',
    },

    jurisdictionState: {
      value: '',
      error: null,
      rule: '',
    },
    operatingAgreement: {
      value: false,
      error: null,
      rule: '',
    },
    operatingAgreementDate: {
      value: null,
      error: null,
      rule: '',
    },
    childEntityName: {
      value: '',
      error: null,
      rule: '',
    },
    childEntityAddress: {
      value: '',
      error: null,
      rule: '',
    },
    childEntityRole: {
      value: '',
      error: null,
      rule: '',
    },
  },
  meta: {
    isValid: true,
    error: null,
  },
};

export class BorrowerEntityStore extends FormStore {
  globalStore: GlobalStore;
  loanStore: LoanStore;
  documentStore: DocumentStore;
  reviewLogStore: ReviewLogStore;

  editEntityBorrowerStore: EditEntityBorrowerStore;
  entityBorrowersGridStore: GridStore;

  borrowerEntityService: BorrowerEntityService;

  showCertificateOfGoodStandingWarning: boolean = false;
  showForeignEntityStatusWarning: boolean = false;

  // role === 'closingAttorney'

  public borrowerEntity: any;
  private entityBorrowers: Array<any>;
  private document: Document;
  private addressFields = ['streetNumber', 'streetName', 'city', 'state', 'zip', 'addressLine2'];
  public automationStatus: any;
  public borrowerEntities: Array<any>;
  public selectedBorrowerEntityId: any;

  constructor(
    loanStore: LoanStore,
    documentStore: DocumentStore,
    globalStore: GlobalStore
  ) {
    super(form, globalStore);
    this.loanStore = loanStore;
    this.documentStore = documentStore;
    this.globalStore = globalStore;
    this.reviewLogStore = new ReviewLogStore(this.globalStore);

    this.editEntityBorrowerStore = new EditEntityBorrowerStore(globalStore, this.reviewLogStore);
    this.entityBorrowersGridStore = new GridStore(
      this.fetchBorrowerEntityBorrowers
    );

    this.borrowerEntityService = new BorrowerEntityService();
    makeObservable(this, {
      canEdit: computed,
      automationStatusMappingValue:computed,
      automatedReadOnlyForm:computed,
      showCertificateOfGoodStandingWarning: observable,
      showForeignEntityStatusWarning: observable,
      checkForWarnings: action,
      hideWarnings: action,
      closeDialog: action,
      editBorrower: action,
      updateAddressFromFields: action,
      saveBorrower: flow,
      initForm: flow,
      submit: flow,
      isClosingAttorney: computed,
      borrowerEntity:observable,
      automationStatus:observable,
      fetchBorrowerEntityAutomationStatus:flow,
      borrowerEntities: observable,
      selectedBorrowerEntityId: observable,
      fetchBorrowerEntities: flow,
      setSelectedBorrowerEntity: action,
      clearSelectedEntity: action,
    });
  }

  fetchBorrowerEntityBorrowers = async () => {
    if (!this.entityBorrowers) {
      const response = await this.borrowerEntityService.getBorrowerEntityBorrowers(
        this.document.loanId
      );
      const responseData = JSON.parse(response.data.data);
      this.entityBorrowers = responseData.data.documentBorrowerEntityMembers;
    }
    const rows = this.entityBorrowers;
    return { headers: null, data: { data: { rows } } };
  };

  private mapEntityToFormShape(entity: any) {
    let borrowerEntity = { ...(entity ?? {}) };
    const company = entity?.company;

    borrowerEntity.entityName = company?.name ?? '';
    borrowerEntity.name = company?.name ?? '';

    borrowerEntity.address =
      company?.noMatchGoogleAddress || (company?.address ?? '');
    borrowerEntity.streetNumber = company?.streetNumber ?? '';
    borrowerEntity.streetName = company?.streetName ?? '';
    borrowerEntity.addressLine2 = company?.aptNumber ?? '';
    borrowerEntity.state = company?.state ?? '';
    borrowerEntity.city = company?.city ?? '';
    borrowerEntity.zip = company?.zipCode ?? '';

    return borrowerEntity;
  }

  private getBorrowerEntityFromLoanDetails() {
    return this.mapEntityToFormShape(this.loanStore.loanDetails.borrowerEntity);
  }

  *fetchBorrowerEntities() {
    try {
      if (this.globalStore.isInternalPortal || this.globalStore.isAttorneyPortal) {
        const response = yield this.borrowerEntityService.getBorrowerEntitiesByLenderId(
          this.loanStore.loanDetails.lenderId
        );
        this.borrowerEntities = response?.data?.data ?? [];
      } else {
        const response = yield this.borrowerEntityService.getAllEntities();
        this.borrowerEntities = response?.data?.data?.rows ?? [];
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching borrower entities',
      });
    }
  }

  clearSelectedEntity = () => {
    this.selectedBorrowerEntityId = null;
    this.borrowerEntity = {};
    this.automationStatus = null;
    this.reset();
  };

  setSelectedBorrowerEntity(borrowerEntityId) {
    const entity = (this.borrowerEntities || []).find(
      e => e.borrowerEntityId === borrowerEntityId
    );
    if (!entity) return;

    const borrowerEntity = this.mapEntityToFormShape(entity);
    this.borrowerEntity = borrowerEntity;
    this.selectedBorrowerEntityId = borrowerEntity.borrowerEntityId;

    this.loadForm({
      ...borrowerEntity,
      borrowerEntityOption: {
        label: borrowerEntity.name,
        value: borrowerEntity.borrowerEntityId,
      },
      cogsDate: borrowerEntity.cogsDate ?? null,
      operatingAgreementDate: borrowerEntity.operatingAgreementDate ?? null,
    });

    this.fetchBorrowerEntityAutomationStatus();
  }

  *initForm(document: Document) {
    this.document = document;
    this.reset();
    this.entityBorrowers = null;
    this.borrowerEntity = null;
    this.borrowerEntities = null;

    // loanDetails is only as fresh as the last page load
    yield this.loanStore.fetchLoanDetails(document?.loanId);

    const borrowerEntity = this.getBorrowerEntityFromLoanDetails();

    this.borrowerEntity = borrowerEntity;
    this.selectedBorrowerEntityId = document?.borrowerEntityId ?? borrowerEntity?.borrowerEntityId ?? null;

    this.loadForm({
      ...borrowerEntity,
      borrowerEntityOption: borrowerEntity?.name
        ? { label: borrowerEntity.name, value: borrowerEntity.borrowerEntityId }
        : null,
      cogsDate: borrowerEntity.cogsDate ?? null,
      operatingAgreementDate: borrowerEntity.operatingAgreementDate ?? null,
    });
    yield this.fetchBorrowerEntities();
    yield this.entityBorrowersGridStore.resetAndFetchGridData();
  }

  closeDialog = () => {
    this.documentStore.documentFormStore.setCurrentForm(null);
  };

  fetchBorrowerEntityAutomationStatus = async () => {
    const borrowerEntityId = this.borrowerEntity?.borrowerEntityId;
    if (!borrowerEntityId) {
      return;
    }
    const response = await this.borrowerEntityService.getBorrowerEntityAutomationStatus(
      borrowerEntityId
    );
    this.automationStatus = response?.data?.data ?? '';
  };

  *submit() {
    this.runFormValidationWithMessage();
    if (this.form.meta.isValid) {
      try {
        this.reviewLogStore.logEvents(this.document.loanId, AIDocType.ENTITY);
        const formValues = this.getFormValues();
        yield this.borrowerEntityService.updateBorrowerEntityForm({
          loanDocument: this.document,
          loanId: this.document.loanId,
          borrowerEntityFormDetails: {
            borrowerEntityId: this.selectedBorrowerEntityId,
            noMatchGoogleAddress: this.borrowerEntity.noMatchGoogleAddress,
            borrowerEntityAddress: this.borrowerEntity.borrowerEntityAddress,
            ...formValues,
            cogsDate: formatDate(formValues.cogsDate, 'MM/dd/yyyy'),
            operatingAgreementDate: formatDate(
              formValues.operatingAgreementDate,
              'MM/dd/yyyy'
            ),
          },
        });
        yield this.borrowerEntityService.saveEntityBorrowers(
          this.document.loanId,
          {
            documentBorrowerEntityMembers: this.entityBorrowers,
          }
        );
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Borrower entity form saved successfully',
        });
        yield this.loanStore.fetchLoanDetails(this.document.loanId);
        this.closeDialog();
        this.checkForWarnings();
      } catch (error) {
        this.globalStore.notificationStore.showErrorNotification({
          message: 'Error while submitting borrower entity form',
        });
      }
    }
  }

  editBorrower(data) {
    this.editEntityBorrowerStore.openDialog(data);
  }

  *saveBorrower() {
    const formValues = this.editEntityBorrowerStore.getFormValues();
    const oldBorrower = this.editEntityBorrowerStore.entityBorrower;
    const newBorrower = { ...oldBorrower, ...formValues };

    const borrowerIndex = this.entityBorrowers.findIndex(
      b => b.borrowerId === newBorrower.borrowerId
    );
    const newBorrowerList = [...this.entityBorrowers];
    newBorrowerList[borrowerIndex] = newBorrower;

    this.entityBorrowers = newBorrowerList;
    this.editEntityBorrowerStore.closeDialog();
    this.entityBorrowersGridStore.fetchGridData();
  }

  saveAddressField(address: string, addressDetails, geometryDetails) {
    const parsedAddress = parseAddress(
      address,
      addressDetails,
      geometryDetails
    );

    this.onFieldChange('streetNumber', parsedAddress.street_number);
    this.onFieldChange('streetName', parsedAddress.street);
    this.onFieldChange('city', parsedAddress.city);
    this.onFieldChange('state', parsedAddress.state);
    this.onFieldChange('zip', parsedAddress.zip);
  }

  onFieldChange(field, value) {
    const eventPayload = {
      docType: AIDocType.ENTITY,
      mapping: 'data',
      itemDisplayName: this.addressFields.includes(field) ? 'address' : field,
      value: this.addressFields.includes(field) ? this.getValue('address') : value
    }
    this.reviewLogStore.addEvent(eventPayload);


    super.onFieldChange(field, value);
  };

  checkForWarnings() {
    const formValues = this.getFormValues();
    const years = differenceInYears(
      new Date(),
      new Date(formValues.operatingAgreementDate)
    );
    if (years > 1) {
      this.showCertificateOfGoodStandingWarning = true;
    }

    const properties = this.loanStore.loanDetails.properties;
    if (properties.some(p => p.state !== formValues.state)) {
      this.showForeignEntityStatusWarning = true;
    }
  }

  hideWarnings() {
    this.showCertificateOfGoodStandingWarning = false;
    this.showForeignEntityStatusWarning = false;
  }

  updateAddressFromFields() {
    const val = this.getFormValues();
    const part1 = [val.streetNumber, val.streetName, val.addressLine2]
      .filter(Boolean)
      .join(' ');
    let address = [part1, val.city, val.state, val.zip]
      .filter(Boolean)
      .join(', ');
    if(address) address = address + ', USA';
    this.onFieldChange('address', address);
  }

  get canEdit() {
    return this.globalStore.isInternalPortal;
  }

  get isClosingAttorney() {
    return this.globalStore.userFeatures?.isClosingAttorney;
  }

  get automationStatusMappingValue() {
    return !!this.automationStatus ? 'Automated': 'Manual'
  }

  get automatedReadOnlyForm() {
    return !!this.automationStatus?.trim();
  }
}

export default BorrowerEntityStore;
